import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'utils/axios';
import { removeEmptyValues } from 'utils/objectUtils';
import { cloneDeep } from 'lodash';
import { operatorsWithoutValue } from 'utils/dataGrid';

export const getLinksList = createAsyncThunk('links/getLinksList', async (_, { rejectWithValue, getState }) => {
  try {
    const {
      auth: { domainID = '' },
      filters: {
        links: {
          filters: currentFilters = [],
          searchValue: currentSearchValue = '',
          page: currentPage = 0,
          sortData: { field: sort = 'id', sort: sort_direction = 'desc' } = {},
          perPage: currentRowCount = 25,
          filterLink: currentFilterLink = 'and',
        } = {},
      } = {},
    } = getState();

    const apiFilters = cloneDeep(currentFilters);
    apiFilters.map((filter) => {
      if (filter.column === 'size') {
        filter.value = Number(filter.value) * 1024 * 1024;
      }
      return filter;
    });

    //  search with keywords, check input value nullable
    const perPageIdxSearching = !currentSearchValue.trim() ? currentPage : 0;

    const queryParams = {
      page: perPageIdxSearching,
      per_page: currentRowCount,
      search: currentSearchValue,
      filters: apiFilters.filter(
        ({ value, operator }) => operatorsWithoutValue.includes(operator) ||
        (!Array.isArray(value) && value) ||
        (Array.isArray(value) && value.length),
      ),
      filter_link: currentFilterLink,
      sort,
      sort_direction,
    };

    const response = await axiosInstance.get(`/${domainID}/links`, {
      params: removeEmptyValues(queryParams),
      data: {},
    });

    return response.data;
  } catch (err) {
    return rejectWithValue(err.response.data);
  }
});

export const getLinkAttributes = createAsyncThunk('links/getLinkAttributes', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get('/attributes?custom=true');
    return response.data;
  } catch (err) {
    return rejectWithValue(err.response.data);
  }
});

export const getSystemLinkAttributes = createAsyncThunk(
  'links/getSystemLinkAttributes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/attributes');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const createLink = createAsyncThunk(
  'links/createLink',
  async ({ linkData = {} }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();
      const response = await axiosInstance.post(`/${domainID}/links`, linkData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const editLink = createAsyncThunk(
  'links/editLink',
  async ({ linkData = {}, linkID = '' }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();
      const response = await axiosInstance.put(`/${domainID}/links/${linkID}`, linkData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const importLinks = createAsyncThunk(
  'links/importLinks',
  async ({ linksFileData = {} }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();
      const response = await axiosInstance.post(`/${domainID}/links/import`, linksFileData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getLinkAttributeMapping = createAsyncThunk(
  'links/getLinkAttributeMapping',
  async ({ uploadID = '' }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();
      const response = await axiosInstance.get(`/${domainID}/links/import/${uploadID}`);
      const invalidHeadersResponse = await axiosInstance.get(`/${domainID}/links/import/map/${uploadID}`);
      return { attributeMapping: response.data, invalidHeaders: invalidHeadersResponse.data };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fillLinkImportData = createAsyncThunk(
  'links/fillLinkImportData',
  async ({ importData = {} }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();
      const response = await axiosInstance.post(`/${domainID}/links/import/`, importData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateVariantsLink = createAsyncThunk(
  'links/generateVariantsLink',
  async ({ linkID, attributesList }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();

      const response = await axiosInstance.post(`/${domainID}/links/variants/${linkID}`, {
        attributes: attributesList,
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const duplicateLink = createAsyncThunk(
  'links/duplicateLink',
  async ({ linkID, count }, { rejectWithValue, getState }) => {
    try {
      const {
        auth: { domainID = '' },
      } = getState();

      const response = await axiosInstance.post(`/${domainID}/links/duplicate/${linkID}`, {
        count,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getRulesBaseStructure = createAsyncThunk('links/rules', async (_, { rejectWithValue, getState }) => {
  try {
    const {
      auth: { domainID = '' },
    } = getState();

    const response = await axiosInstance.get(`/${domainID}/links/rules`);
    return response.data;
  } catch (err) {
    return rejectWithValue(err.response.data);
  }
});
